import React, { useState } from 'react';
import { X, FileSignature, Loader2, CheckCircle } from 'lucide-react';
import SignaturePad from '../engagement/SignaturePad';
import { recordPortalDeliverableApproval } from '../../utils/portalDeliverableApprovalService';
import './PortalDeliverableApprovalModal.css';

export interface PortalDeliverableApprovalModalProps {
  open: boolean;
  onClose: () => void;
  portalId: string;
  dealId: string;
  deliverableType: 'plans' | 'model' | 'concept';
  deliverableLabel: string;
  releaseId?: string;
  bundleId?: string;
  releaseVersionIndex?: number;
  /** All releases covered (bundle members) — stamped per release for the approval trail. */
  releaseIds?: string[];
  /** Set when this 3D model is the same upload on more than one structure group. */
  sharedModelNote?: string;
  onApproved?: () => void;
}

const PortalDeliverableApprovalModal: React.FC<PortalDeliverableApprovalModalProps> = ({
  open,
  onClose,
  portalId,
  dealId,
  deliverableType,
  deliverableLabel,
  releaseId,
  bundleId,
  releaseVersionIndex,
  releaseIds,
  sharedModelNote,
  onApproved,
}) => {
  const [signatureData, setSignatureData] = useState<{ dataUrl: string; signedBy: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!signatureData) return;
    setSubmitting(true);
    setError(null);
    try {
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType,
        signature: signatureData,
        releaseId,
        bundleId,
        releaseVersionIndex,
        releaseIds,
      });
      setDone(true);
      onApproved?.();
    } catch (e) {
      console.error(e);
      setError('Could not record your approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pda-overlay" role="presentation" onClick={onClose}>
      <div
        className="pda-shell"
        role="dialog"
        aria-modal="true"
        aria-label={`Approve ${deliverableLabel}`}
        onClick={e => e.stopPropagation()}
      >
        <header className="pda-header">
          <h2>
            <FileSignature size={18} aria-hidden />
            Approve {deliverableLabel}
          </h2>
          <button type="button" className="pda-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {done ? (
          <div className="pda-success">
            <CheckCircle size={40} aria-hidden />
            <p>Thank you — your approval has been recorded.</p>
            <button type="button" className="pda-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            {sharedModelNote ? (
              <p className="pda-shared-model" role="status">{sharedModelNote}</p>
            ) : null}
            <p className="pda-copy">
              {deliverableType === 'model'
                ? 'This model is ready for your approval. Please confirm the dimensions and sizes match what you expect.'
                : `By signing below, you confirm that you have reviewed the ${deliverableLabel.toLowerCase()} and approve this deliverable for your project. Your project team will be notified.`}
            </p>
            <SignaturePad
              onSign={data => setSignatureData(data)}
              hideSubmitButton
              onChange={setSignatureData}
            />
            {error && <p className="pda-error" role="alert">{error}</p>}
            <footer className="pda-footer">
              <button type="button" className="pda-secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className="pda-primary"
                onClick={() => void handleSubmit()}
                disabled={!signatureData || submitting}
              >
                {submitting ? <Loader2 size={16} className="pda-spin" /> : null}
                Sign &amp; Approve
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalDeliverableApprovalModal;
